import { AppBar, Button, Chip, CircularProgress, Divider, Stack, Toolbar, Typography } from "@mui/material";
import { Navigate, useParams } from "react-router-dom";
import { useFetchSurveyQuery } from "./surveysApi";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useMemo, useState } from "react";
import { East, West } from "@mui/icons-material";
import { Check } from "lucide-react";
import { motion } from "framer-motion"

const SurveyAnswerPage = () => {
    const { organizationId, surveyId } = useParams<{ organizationId: string; surveyId: string }>();
    const survey = useFetchSurveyQuery({
        organizationId: organizationId || skipToken,
        surveyId: surveyId || skipToken,
    });

    const [answers, setAnswers] = useState<Record<string, string[]>>({});
    const [activeQuestion, setActiveQuestion] = useState(0);
    const [direction, setDirection] = useState<'foreward' | 'backward'>('foreward');

    const handlePreviousQuestion = () => {
        setDirection('backward');

        if (activeQuestion === 0) return;
        setActiveQuestion(prev => prev - 1);
    }
    
    const handleNextQuestion = () => {
        setDirection('foreward');

        if (activeQuestion < (survey.data?.questions ?? []).length - 1) {  
            setActiveQuestion(prev => prev + 1);
        }
    }
    
    const handleSubmit = () => {
        //
    }

    const totalQuestions = useMemo(() => {
        return (survey.data?.questions ?? []).length
    }, [survey.data?.questions])
    
    const isFirstQuestion = useMemo(() => {
        return activeQuestion === 0
    }, [activeQuestion])
    
    const isLastQuestion = useMemo(() => {
        return activeQuestion === totalQuestions - 1
    }, [activeQuestion, totalQuestions])

    const hasAnswers = useMemo(() => {
        const question = survey.data?.questions[activeQuestion];
        const questionId: string = question?.id ?? '';
        return (answers[questionId] ?? []).length
    }, [activeQuestion, answers, survey.data?.questions])
    
    if (survey.isLoading) {
        return (
            <Stack flex={1} alignItems="center" py={5}>
                <CircularProgress/>
            </Stack>
        )
    }

    if (!survey.data) {
        return (
            <Navigate to="/not-found" replace={true} />
        )
    }

    return (
        <Stack
            sx={{
                backgroundColor: 'white',
                height: '100dvh',
                width: '100vw'
            }}
        >
         
            <AppBar position="static" color="default">
                <Toolbar>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" flex={1}>
                        <Typography variant="h4">{survey.data.title}</Typography>
                        {activeQuestion + 1} / {totalQuestions}
                    </Stack>
                </Toolbar>
            </AppBar>
            <Stack
                px={2}
                py={4}
                alignItems="center"
                justifyContent="center"
            >
                <Typography
                    key={`question-${activeQuestion}`}
                    variant="h1"
                    align="center"
                    component={motion.div}
                    initial={{
                        opacity: 0,
                        x: direction === 'foreward' ? '100vw' : '-100vw'
                    }}
                    animate={{
                        opacity: 1,
                        x: 0
                    }}
                    transition={{
                        delay: 0.15,
                        duration: 0.5
                    }}
                >
                    {survey.data.questions[activeQuestion].title}
                </Typography>
            </Stack>
            <Stack
                flex={1}
                spacing={2}
                padding={2}
                alignItems="center"
                justifyContent="center"
            >
                <Stack
                    spacing={2}
                >
                    {survey.data.questions[activeQuestion].options.map((option, optionIndex) => {
                        const question = survey.data?.questions[activeQuestion];
                        const questionId: string = question?.id ?? '';
                        const currentValues = answers[questionId] ?? [];
                        const index = currentValues.findIndex((id) => id === option.id);
                        const isOptionIncluded = index >= 0;

                        return (
                            <Chip
                                key={`question-${activeQuestion}-option-${optionIndex}`}
                                label={option.title}
                                clickable
                                color={isOptionIncluded ? "success" : "primary"}
                                variant={isOptionIncluded ? "filled" : "outlined"}
                                onClick={() => {
                                    setAnswers(prev => {
                                        if (isOptionIncluded) {
                                            currentValues.splice(index, 1);
                                        } else {
                                            currentValues.push(option.id);
                                        }

                                        return {
                                            ...prev,
                                            [questionId]: currentValues,
                                        };
                                    })
                                }}
                                component={motion.div}
                                initial={{
                                    opacity: 0,
                                    x: direction === 'foreward' ? '100vw' : '-100vw'
                                }}
                                animate={{
                                    opacity: 1,
                                    x: 0
                                }}
                                transition={{
                                    delay: 0.25,
                                    duration: 0.5
                                }}
                            />
                        );
                    })}
                </Stack>
            </Stack>
            <Divider/>
            <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                padding={2}
                spacing={2}
            >
                <Button
                    fullWidth
                    startIcon={<West/>}
                    size="large"
                    variant="outlined"
                    onClick={handlePreviousQuestion}
                    disabled={isFirstQuestion}
                    component={motion.div}
                    whileHover={{
                        scale: 1.125,
                    }}
                >
                    Anterior
                </Button>
                { isLastQuestion ? (
                    <Button
                        fullWidth
                        color="success"
                        endIcon={<Check />}
                        size="large"
                        onClick={handleSubmit}
                        disabled={!hasAnswers}
                        component={motion.div}
                        whileHover={{
                            scale: 1.125,
                        }}
                    >
                        Finalizar
                    </Button>
                ) : (
                    <Button
                        fullWidth
                        endIcon={<East/>}
                        size="large"
                        onClick={handleNextQuestion}
                        disabled={!hasAnswers}
                        component={motion.div}
                        whileHover={{
                            scale: 1.125,
                        }}
                    >
                        Siguiente
                    </Button>
                )}
            </Stack>
        </Stack>
    );
}

export default SurveyAnswerPage;
